
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { UserPermissions, FeaturesList, TimeZone, UlType } from "@/store/types";
import api from "@/services/api.service";
import { get } from "lodash";
import { Group, GroupWithPermissions } from "@/store/areas/types";
import NavHeader from "@/components/NavHeader.vue";
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import { ModalItem } from "@/components/table/generic-update-modal.vue";
import { filter } from "rxjs/operators";
import { areas } from "../store/areas";
import TextHighlight from "vue-text-highlight";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";

import { SiteTreeNode } from "@/store/eventqueue/types";

import AreaSetupDisableAreaButton from "@/components/areas/AreaSetupDisableAreaButton.vue";
import tree from "../components/Tree.vue";
import { async } from "rxjs/internal/scheduler/async";

const Areas = namespace("areas");
const Eventqueue = namespace("eventqueue");
const GenericTableStore = namespace("GenericTable");

@Component({
	components: {
		"nav-header": NavHeader,
		"generic-table": GenericTable,
		"disable-area-button": AreaSetupDisableAreaButton,
		"text-highlight": TextHighlight
	}
})
export default class AreaSetup extends Vue {
	@Getter getPermissions: UserPermissions;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getTimeZones") timeZoneList: TimeZone[];
	@Getter("getUlTypes") ulTypeList: UlType[];
	@State(State => State.User.allowEmptyAddressesOnAreas) allowEmptyAddressesOnAreas: boolean;
	@Mutation private setIsLoading: (loading: boolean) => void;

	@Action getTimeZones: any;
	@Action getUlTypes: ()=>Promise<void>;

	@Eventqueue.Getter getSiteTree: SiteTreeNode[];
	@Eventqueue.Getter getSelectedGroup: any;
	@Eventqueue.Action sitesTreeGet: any;
	@Eventqueue.Mutation setTreeGroupSelected: any;

	@Areas.Action getAreaList: any;
	@Areas.Action updateArea: any;
	@Areas.Action deleteArea: any;
	@Areas.Action createArea: any;

	@GenericTableStore.Getter getModalRow: any;
	@GenericTableStore.Mutation setModalRow: any;

	@Areas.Getter("getAreaList") areaList!: any[];

	private TimeZones = [];

	private groupTypes = [{ groupTypeID: 1, title: "Area" }, { groupTypeID: 2, title: "Region" }];

	private VisibleSites = [];

	private isLoading: boolean = false;

	private UlTypes = [];

	private canEditAreaType: boolean = true;

	// List of the areas returned from the data service
	public AreaList: GroupWithPermissions[] = [];

	// If feature is on / off
	public get IsAreaManagementEnabled() {
		return get(this.featuresList, ["Areas"]);
	}

	public get IsAreaUlSettingsEnabled(): boolean {
		return !!get(this.featuresList, ["Areas", "UlAreaSettings"]);
	}

	private get isDisableManualRaiseEnabled() {
		return !!get(this.featuresList, ["Alarms", "EventQueue", "ManualRaise", "DisableOnArea"], false);
	}

	private get isSelectAreaTypeEnabled() {
		return !!get(this.featuresList, ["Areas", "SelectAreaType"], false);
	}

	async mounted() {
		this.isLoading = true;
		if (this.IsAreaManagementEnabled) {
			await this.PopulateAreaList(true);

			await this.getTimeZones();

			if (this.timeZoneList) {
				// get timezones and map to the format needed to v-select
				this.TimeZones = this.timeZoneList.map(t => {
					return { timeZoneID: t.timeZoneID, title: t.id };
				});
			}

			await this.getUlTypes();

			if (this.ulTypeList) {
				//get UlTypes and map to the format needed to v-select
				this.UlTypes = this.ulTypeList.map(t => {
					return { ulTypeID: t.ulTypeId, title: t.title };
				});			
			}			
		}
		this.isLoading = false;
	}

	public async PopulateAreaList(refreshVisisbleAreas: boolean) {
		await this.getAreaList();

		this.AreaList = this.areaList;
		if (refreshVisisbleAreas) {
			this.AreaList.filter(area => {
				if (area.isOpen) {
					this.VisibleSites.push(area.groupID);
					if (area.hasChildren) {
						this.showChildren(area.groupID);
					}
				} else {
					this.hideChildren(area.groupID);
				}
			});
		}
	}

	private async DeleteAreaConfirmed(area: Group) {
		// delete the choosen area
		this.deleteArea(area.groupID).then(async r => {
			// refresh the AreaList to reflect the change
			await this.PopulateAreaList(true);
		});
	}

	private get TableHeadings() {
		const tableHeadings = [
			{
				title: "Title",
				key: "title",
				order: 0,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Title of the area",
				searchable: true,
				visible: true,
				dataType: "text",
				useCustomCell: true,
				width: "20%"
			} as TableHeader,
			{
				title: "Address",
				key: "address",
				order: 1,
				sortOrder: 1,
				sortOrderReversed: false,
				description: "Address of the area",
				searchable: true,
				visible: true,
				dataType: "text"
			} as TableHeader,
			{
				title: "Telephone",
				key: "telephone",
				order: 2,
				sortOrder: 2,
				sortOrderReversed: false,
				description: "Primary Telephone Number",
				searchable: true,
				visible: true,
				dataType: "telephone"
			} as TableHeader,
			{
				title: "Alt. Telephone",
				key: "telephone2",
				order: 3,
				sortOrder: 3,
				sortOrderReversed: false,
				description: "Secondary Telephone Number",
				searchable: true,
				visible: true,
				dataType: "telephone"
			} as TableHeader,
			{
				title: "Telephone Fire",
				key: "telephoneFire",
				order: 4,
				sortOrder: 4,
				sortOrderReversed: false,
				description: "Secondary Telephone Number",
				searchable: true,
				visible: false,
				dataType: "telephone"
			} as TableHeader,
			{
				title: "Telephone Police",
				key: "telephonePolice",
				order: 5,
				sortOrder: 5,
				sortOrderReversed: false,
				description: "Secondary Telephone Number",
				searchable: true,
				visible: false,
				dataType: "telephone"
			} as TableHeader,
			{
				title: "Time Zone",
				key: "timeZoneID",
				order: 6,
				sortOrder: 6,
				sortOrderReversed: false,
				description: "Secondary Telephone Number",
				searchable: false,
				visible: false,
				dataType: "text",
				useCustomCell: true
			} as TableHeader,
			{
				title: "Summary",
				key: "notes",
				order: 9,
				sortOrder: 9,
				sortOrderReversed: false,
				description: "Important information regarding the Area",
				searchable: true,
				visible: true,
				dataType: "text",
				width: "30%"
			} as TableHeader,
			{
				title: "Status",
				key: "disabledAt",
				order: 10,
				sortOrder: 10,
				sortOrderReversed: false,
				description: "Secondary Telephone Number",
				searchable: false,
				visible: false,
				dataType: "text",
				useCustomCell: true
			} as TableHeader
		];

		if(this.isDisableManualRaiseEnabled)
			tableHeadings.push({
				title: "Manual Raise Enabled",
				key: "canManualRaise",
				order: 11,
				sortOrder: 11,
				sortOrderReversed: false,
				description: "Flag indicating if manual raises can be triggered on this area",
				searchable: false,
				visible: false,
				dataType: "text",
				useCustomCell: true
			} as TableHeader);

		return tableHeadings;
	}

	private getAreaLevel(level, area) {
		if (!area || !area.parentGroupID) {
			return level;
		} else {
			var parent = this.AreaList.find(a => {
				return a.groupID == area.parentGroupID;
			});
			let newLevel = level + 1;
			return this.getAreaLevel(newLevel, parent);
		}
	}

	private GetModalItems() {
		var modalItems = [
			{
				title: "Title",
				key: "title",
				dataType: "text",
				placeholder: "Area Title",
				required: true,
				maxLength: 50
			},
			{
				title: "Address",
				key: "address",
				dataType: "text",
				readOnly: false,
				newOnly: false,
				required: !this.allowEmptyAddressesOnAreas,
				placeholder: "123 High Street",
				maxLength: 200
			},
			{
				title: "Telephone",
				key: "telephone",
				dataType: "text",
				readOnly: false,
				maxLength: 50
			},
			{
				title: "Alt. Telephone",
				key: "telephone2",
				dataType: "text",
				readOnly: false,
				maxLength: 50
			},
			{
				title: "Telephone Fire",
				key: "telephoneFire",
				dataType: "text",
				readOnly: false,
				maxLength: 50
			},
			{
				title: "Telephone Police",
				key: "telephonePolice",
				dataType: "text",
				readOnly: false,
				maxLength: 50
			},
			{
				title: "Time Zone",
				key: "timeZoneID",
				dataType: "select",
				readOnly: false,
				data: this.TimeZones,
				placeholder: "Choose Timezone Area",
				required: true
			},
			{
				title: "Parent Area",
				key: "parentGroupID",
				dataType: "component",
				readOnly: false,
				data: AreaTreeSelect,
				props: {
					reduce: node => node.id,
					appendToBody: true
				},
				required: this.isParentRequired(),
				visible: this.isParentRequired()
			},
			{
				title: "Area Status",
				key: "disabledAt",
				dataType: "component",
				readOnly: false,
				data: AreaSetupDisableAreaButton,
				updateOnly: true
			},
			{
				title: "Summary",
				key: "notes",
				dataType: "textarea",
				readOnly: false,
				placeholder: "Area Notes..."
			},
			{
				title: "Manual Raise enabled",
				key: "canManualRaise",
				dataType: "checkbox",
				visible: this.isDisableManualRaiseEnabled,
				readOnly: false,
				defaultValue: true
			},
			{
				title: "Station Has Keys?",
				key: "haveKeys",
				dataType: "checkbox",
				visible: this.IsAreaUlSettingsEnabled,
				readOnly: false,
				required: false
			},
			{
				title: "UL Type",
				key: "ulTypeID",
				dataType: "select",
				visible: this.IsAreaUlSettingsEnabled,
				readOnly: false,
				data: this.UlTypes,
				placeholder: "Choose UL Type",
				required: false
			},
			{
				title: "Is Area Active?",
				key: "isActive",
				dataType: "checkbox",
				visible: this.IsAreaUlSettingsEnabled,
				readOnly: false,
				defaultValue: false
			},
			{
				title: "Is Line Secure?",
				key: "isSecureLine",
				dataType: "checkbox",
				visible: this.IsAreaUlSettingsEnabled,
				readOnly: false,
				defaultValue: false
			},
			{
				title: "Is Line Encrypted?",
				key: "isEncrypted",
				dataType: "checkbox",
				visible: this.IsAreaUlSettingsEnabled,
				readOnly: false,
				defaultValue: false
			},
			{
				title: "Is Investigatior Required?",
				key: "investigatorRequired",
				dataType: "checkbox",
				visible: this.IsAreaUlSettingsEnabled,
				readOnly: false,
				defaultValue: false
			},
			{
				title: "Response Time (Minutes)",
				key: "responseTime",
				dataType: "number",
				visible: this.IsAreaUlSettingsEnabled,
				readOnly: false,
				minValue: 0, 
				maxValue: 32767
			}
		] as ModalItem[];

		if (this.isSelectAreaTypeEnabled && this.canEditAreaType) {
			modalItems.splice(2, 0, {
				title: "Area Type",
				key: "groupTypeID",
				dataType: "select",
				readOnly: false,
				data: this.groupTypes,
				placeholder: "Choose an area type",
				required: true
			});
		}

		return modalItems;
	}

	private showChildren(areaId) {
		this.toggleTreeIconState(areaId, "open");
		this.getChildren(areaId).forEach(child => {
			this.VisibleSites.push(child.groupID);
		});
	}

	private hideChildren(areaId) {
		this.toggleTreeIconState(areaId, "closed");

		this.getChildren(areaId).forEach(child => {
			if (child.hasChildren) {
				this.hideChildren(child.groupID);
			}

			this.VisibleSites = this.VisibleSites.filter(f => {
				return f != child.groupID;
			});
		});
	}

	private toggleTreeIconState(areaId, state) {
		var area = this.AreaList.find(a => {
			return a.groupID == areaId;
		});

		area.isOpen = state == "open" ? true : false;
	}

	private getAllChildren(areaId) {
		let all = [];
		this.getChildren(areaId, all);
		return all;
	}

	private getChildren(areaId, arr = null) {
		let c = this.AreaList.filter(area => {
			return area.parentGroupID == areaId;
		});
		if (arr) {
			c.forEach(element => {
				arr.push(element.groupID);
				return this.getChildren(element.groupID, arr);
			});
		} else {
			return c;
		}
	}

	private get getVisibleAreas() {
		let areas = this.AreaList.map(area => {
			return {
				...area,
				hidden: !this.VisibleSites.includes(area.groupID),
				deleteDisabled: area["disabledAt"] == null || area["disabledAt"] == undefined,
				deleteDisabledMessage: `${area.title} must be disabled before it can be deleted`,
				timeZoneID: this.TimeZones.find(f => {
					return f.timeZoneID == area.timeZoneID;
				}),
				ulTypeID: this.UlTypes?.find(f => {
					return f.ulTypeID == area.ulTypeID;
				}) ?? 0,
				groupTypeID: this.groupTypes.find(groupType => groupType.groupTypeID == area.groupTypeID),
			};
		});

		return areas;
	}

	private areaHasChilden(areaId) {
		return this.AreaList.some(area => {
			return area.parentGroupID == areaId;
		});
	}

	private async openUpdateModal(row) {
		if (row) {
			// areas with a groupTypeID of 4 cannot have parents
			if (row.groupTypeID && row.groupTypeID.groupTypeID != 4) {
				this.parentIsRequired = true;
			}
			else {
				this.parentIsRequired = false;
			}
			
			// areas with a groupTypeID of less than 3 cannot have their area type edited
			this.canEditAreaType = row.groupTypeID?.groupTypeID < 3;
		}
		else {
			this.parentIsRequired = true;
			this.canEditAreaType = true;
		}
	}

	private getGroupFromTree(tree: any[], groupId: number) {
		let group: any = {};

		tree.forEach(node => {
			if (node.group.groupID == groupId) {
				group = node.group;
			} else if (!group.hasOwnProperty("groupID") && node.subGroups.length) {
				group = this.getGroupFromTree(node.subGroups, groupId);
			}
		});
		return group;
	}

	public async addNewArea(area) {
		// if a timezone is set use that
		if (area && area.timeZoneID) {
			// convert v-select object back to just the id for the timezone
			area.timeZoneID = area.timeZoneID.timeZoneID;

			// otherwise use parent timezone
		} else {
			let parent = this.getGroupFromTree(await this.getSiteTree, area.parentGroupID);

			if (parent) {
				area.timeZoneID = parent.timeZoneID;
			}
		}

		if (area && area.ulTypeID) {
			// convert v-select object back to just the id for the timezone
			area.ulTypeID = area.ulTypeID.ulTypeID;

			// otherwise use parent timezone
		} else {
			area.ulTypeID = 0;
		}

		// convert v-select object back to just the id for the group/area type
		if (area?.groupTypeID) {
			area.groupTypeID = area.groupTypeID.groupTypeID;
		} else {
			area.groupTypeID = 1; // Regular area
		}

		let newArea = {
			groupID: 0,
			groupTypeID: area.groupTypeID,
			parentGroupID: area.parentGroupID,
			title: area.title,
			armed: true,
			toggleArmAt: area.toggleArmAt,
			disarmReasonID: area.disarmReasonID,
			licenseValid: area.licenseValid,
			licenseTypeID: area.licenseTypeID,
			disabledAt: area.disabledAt,
			notes: area.notes,
			address: area.address,
			telephone: area.telephone,
			telephone2: area.telephone2,
			telephonePolice: area.telephonePolice,
			telephoneFire: area.telephoneFire,
			timeZoneID: area.timeZoneID,
			canManualRaise: area.canManualRaise,
			haveKeys: area.haveKeys || false,
			ulTypeID: area.ulTypeID,
			isActive: area.isActive || false,
			isSecureLine: area.isSecureLine || false,
			isEncrypted: area.isEncrypted || false,
			investigatorRequired: area.investigatorRequired || false,
			responseTime: area.responseTime || 0
		};

		await this.createArea(newArea).then(async r => {
			await this.PopulateAreaList(true);
		});
	}

	public async updateAreaDetails(area) {
		// convert v-select object back to just the id for the timezone
		area.timeZoneID = typeof area.timeZoneID === "object" ? area.timeZoneID.timeZoneID : area.timeZoneID;

		area.ulTypeID = typeof area.ulTypeID === "object" ? area.ulTypeID.ulTypeID : area.ulTypeID;

		area.groupTypeID = typeof area.groupTypeID === "object" ? area.groupTypeID.groupTypeID : area.groupTypeID
		
		let UpdatedArea = {
			groupID: area.groupID,
			parentGroupID: area.parentGroupID,
			groupTypeID: area.groupTypeID,
			title: area.title,
			armed: true,
			toggleArmAt: area.toggleArmAt,
			disarmReasonID: area.disarmReasonID,
			licenseValid: area.licenseValid,
			licenseTypeID: area.licenseTypeID,
			disabledAt: area.disabledAt,
			notes: area.notes,
			address: area.address,
			telephone: area.telephone,
			telephone2: area.telephone2,
			telephonePolice: area.telephonePolice,
			telephoneFire: area.telephoneFire,
			timeZoneID: area.timeZoneID,
			canManualRaise: area.canManualRaise,
			haveKeys: area.haveKeys,
			ulTypeID: area.ulTypeID,
			isActive: area.isActive,
			isSecureLine: area.isSecureLine,
			isEncrypted: area.isEncrypted,
			investigatorRequired: area.investigatorRequired,
			responseTime: area.responseTime
		};
		
		await this.updateArea(UpdatedArea).then(async r => {
			await this.PopulateAreaList(false);
		});
	}

	private getTimeZoneTitle(timeZone) {
		let zone = this.TimeZones.find(f => {
			return f.timeZoneID == timeZone.timeZoneID;
		});
		return zone.title;
	}

	private getGroupTypeTitle(groupTypeID) {	
		let groupType = this.groupTypes.find(f => {
			return f.groupTypeID == groupTypeID;
		});
		return groupType.title;
	}

	private getUlTypeTitle(ulType) {
		let ul = this.UlTypes.find(f => {
			return f.ulTypeID == ulType.ulTypeID;
		});
		return ul.title;
	}

	private parentIsRequired = true;

	private isParentRequired() {
		return this.parentIsRequired;
	}

	public getRowId() {
		if (this.getModalRow && this.getModalRow.groupID) {
			return this.getModalRow.groupID;
		}
		return "";
	}
}
